import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { CircleDollarSign, Clock3, RefreshCw, Search, ShoppingBag, XCircle } from "lucide-react";
import type { BillingOrderStatus } from "@shudian/contracts";
import { adminListBillingOrders } from "../api/admin.js";
import { Field } from "../components/ui/Field.js";
import { Input } from "../components/ui/Input.js";
import { Button } from "../components/ui/Button.js";
import { Badge } from "../components/ui/Badge.js";
import { Table, TBody, THead, TR, TH, TD, EmptyRow } from "../components/ui/Table.js";
import { FullPageSpinner } from "../components/ui/Spinner.js";
import { Alert } from "../components/ui/Alert.js";
import { MetricCard, PageHeader, SectionHeader, SectionSurface } from "../components/PageScaffold.js";
import { CloudApiError, describeError } from "../lib/errors.js";
import { formatDateTime, formatNumber } from "../lib/format.js";
import { formatLicensePlan } from "../lib/license.js";
import {
  billingOrderStatusLabel,
  billingOrderStatusTone,
  billingPeriodLabel,
  billingProviderLabel,
  formatCurrency
} from "../lib/billing.js";

const ORDER_STATUS_OPTIONS: BillingOrderStatus[] = [
  "pending",
  "paid",
  "failed",
  "refunded",
  "cancelled",
  "expired"
];

export function BillingOrdersPage() {
  const [tenantId, setTenantId] = useState("");
  const [status, setStatus] = useState<BillingOrderStatus | "">("");
  const [limit, setLimit] = useState(100);

  const { data, isLoading, isError, error, refetch, isFetching } = useQuery({
    queryKey: ["admin", "billing-orders", { tenantId, status, limit }],
    queryFn: () =>
      adminListBillingOrders({
        tenantId: tenantId.trim() || undefined,
        status: status || undefined,
        limit
      })
  });

  const orders = data?.orders ?? [];
  const paidCount = orders.filter((order) => order.status === "paid").length;
  const pendingCount = orders.filter((order) => order.status === "pending").length;
  const failedCount = orders.filter((order) => order.status === "failed").length;

  return (
    <div>
      <PageHeader
        eyebrow="订单与套餐 / 订单管理"
        title="订单管理"
        description="查看客户开通和续费订单，优先核对待支付、支付失败和支付回调异常。"
        actions={
          <Button variant="secondary" onClick={() => void refetch()} disabled={isFetching}>
            <RefreshCw className="h-4 w-4" />
            {isFetching ? "刷新中..." : "刷新"}
          </Button>
        }
      />

      <section className="mb-5 grid grid-cols-1 gap-3 md:grid-cols-2 xl:grid-cols-4">
        <MetricCard label="当前列表" value={formatNumber(orders.length)} detail={`最多 ${formatNumber(limit)} 条`} icon={<ShoppingBag className="h-4 w-4" />} />
        <MetricCard label="已支付" value={formatNumber(paidCount)} detail="可用于授权核对" tone="success" icon={<CircleDollarSign className="h-4 w-4" />} />
        <MetricCard label="待支付" value={formatNumber(pendingCount)} detail="需要运营跟进" tone="warning" icon={<Clock3 className="h-4 w-4" />} />
        <MetricCard label="支付失败" value={formatNumber(failedCount)} detail="需要核对支付渠道" tone={failedCount > 0 ? "danger" : "neutral"} icon={<XCircle className="h-4 w-4" />} />
      </section>

      <SectionSurface className="mb-5">
        <SectionHeader title="订单筛选" description="按客户 ID、订单状态和返回数量筛选，后续可扩展订单号和时间范围。" />
        <div className="grid grid-cols-1 gap-4 px-4 py-4 md:grid-cols-4">
          <Field label="客户 ID" htmlFor="billingTenantId">
            <Input
              id="billingTenantId"
              placeholder="留空显示全部"
              value={tenantId}
              onChange={(event) => setTenantId(event.target.value)}
            />
          </Field>
          <Field label="订单状态" htmlFor="billingOrderStatus">
            <select
              id="billingOrderStatus"
              className="h-9 rounded border border-surface-border bg-surface-panel px-2.5 text-sm text-text-primary focus:outline-none focus:ring-2 focus:ring-accent/40 focus:border-accent/70"
              value={status}
              onChange={(event) => setStatus(event.target.value as BillingOrderStatus | "")}
            >
              <option value="">全部</option>
              {ORDER_STATUS_OPTIONS.map((value) => (
                <option key={value} value={value}>
                  {billingOrderStatusLabel(value)}
                </option>
              ))}
            </select>
          </Field>
          <Field label="数量上限" htmlFor="billingOrderLimit" hint="1-200">
            <Input
              id="billingOrderLimit"
              type="number"
              min={1}
              max={200}
              value={limit}
              onChange={(event) => setLimit(Number(event.target.value) || 100)}
            />
          </Field>
          <div className="flex items-end">
            <Button onClick={() => void refetch()} disabled={isFetching}>
              <Search className="h-4 w-4" />
              查询
            </Button>
          </div>
        </div>
      </SectionSurface>

      {isLoading ? (
        <FullPageSpinner />
      ) : isError ? (
        <Alert tone="danger">
          {error instanceof CloudApiError ? describeError(error.code) : "加载订单失败"}
        </Alert>
      ) : (
        <SectionSurface>
          <SectionHeader title={`订单列表 (${formatNumber(data?.orders.length ?? 0)})`} />
          <Table className="rounded-none border-0">
            <THead>
              <TR>
                <TH>订单</TH>
                <TH>用户</TH>
                <TH>套餐</TH>
                <TH>金额</TH>
                <TH>状态</TH>
                <TH>支付渠道</TH>
                <TH>创建时间</TH>
                <TH>支付时间</TH>
              </TR>
            </THead>
            <TBody>
              {data && data.orders.length > 0 ? (
                data.orders.map((order) => (
                  <TR key={order.id}>
                    <TD>
                      <div className="font-mono text-xs text-text-primary">{order.id}</div>
                      <div className="font-mono text-xxs text-text-muted">{order.providerOrderId}</div>
                    </TD>
                    <TD>
                      <Link
                        to={`/tenants/${encodeURIComponent(order.tenantId)}`}
                        className="text-text-primary hover:text-accent"
                      >
                        {order.tenantName}
                      </Link>
                      <div className="font-mono text-xs text-text-muted">{order.tenantId}</div>
                      {order.userEmail ? (
                        <div className="text-xs text-text-secondary">{order.userEmail}</div>
                      ) : null}
                    </TD>
                    <TD>
                      <div className="text-sm text-text-primary">{formatLicensePlan(order.licensePlan)}</div>
                      <div className="text-xs text-text-muted">{billingPeriodLabel(order.billingPeriod)}</div>
                    </TD>
                    <TD className="whitespace-nowrap">{formatCurrency(order.amount, order.currency)}</TD>
                    <TD>
                      <Badge tone={billingOrderStatusTone(order.status)}>
                        {billingOrderStatusLabel(order.status)}
                      </Badge>
                    </TD>
                    <TD>
                      <div className="text-xs text-text-primary">{billingProviderLabel(order.provider)}</div>
                      <div className="font-mono text-xxs text-text-muted">
                        {order.providerTransactionId ?? "-"}
                      </div>
                    </TD>
                    <TD className="whitespace-nowrap text-xs text-text-secondary">
                      {formatDateTime(order.createdAt)}
                    </TD>
                    <TD className="whitespace-nowrap text-xs text-text-secondary">
                      {formatDateTime(order.paidAt)}
                    </TD>
                  </TR>
                ))
              ) : (
                <EmptyRow colSpan={8} label="暂无订单。" />
              )}
            </TBody>
          </Table>
        </SectionSurface>
      )}
    </div>
  );
}
